import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Crown } from 'lucide-react';

interface FeatureItem {
  text: string;
  included: boolean;
}

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: FeatureItem[];
  cta: string;
  ctaLink: string;
  popular?: boolean;
  annualPrice?: string;
}

const tiers: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Free forever',
    features: [
      { text: '1 email account', included: true },
      { text: 'AI vendor detection', included: true },
      { text: 'Smart organization', included: true },
      { text: 'Manual refresh', included: true },
      { text: '7-day email retention', included: true },
      { text: 'No auto-refresh', included: false },
      { text: 'No auto-delete', included: false },
      { text: 'No NUKE feature', included: false },
    ],
    cta: 'Get Started',
    ctaLink: '/login',
  },
  {
    name: 'Pro',
    price: '$9',
    period: '/month',
    description: 'Everything you need',
    annualPrice: '$7/month billed annually',
    features: [
      { text: 'Up to 5 email accounts', included: true },
      { text: 'AI vendor detection', included: true },
      { text: 'Smart organization', included: true },
      { text: 'Auto-refresh every 15 minutes', included: true },
      { text: 'Auto-delete with custom retention', included: true },
      { text: 'NUKE feature - delete all vendor emails', included: true },
      { text: '30-day email retention', included: true },
      { text: 'Priority support', included: true },
    ],
    cta: 'Start Pro Trial',
    ctaLink: '/login',
    popular: true,
  },
];

const PricingSection: React.FC = () => {
  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Start free, upgrade when you need more power
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl p-8 ${
                tier.popular
                  ? 'bg-white ring-2 ring-violet-600 shadow-xl scale-105 z-10'
                  : 'bg-white border border-gray-200 shadow-md'
              }`}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="inline-flex items-center gap-1.5 bg-violet-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full">
                    <Crown className="w-4 h-4" />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Tier Header */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {tier.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-gray-900">
                    {tier.price}
                  </span>
                  <span className="text-gray-500">{tier.period}</span>
                </div>
                {tier.annualPrice ? (
                  <p className="text-sm text-violet-600 font-medium mt-2">
                    {tier.annualPrice}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-2">{tier.description}</p>
                )}
              </div>

              {/* Features List */}
              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span
                      className={
                        feature.included ? 'text-gray-700' : 'text-gray-400'
                      }
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Link
                to={tier.ctaLink}
                className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                  tier.popular
                    ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-lg hover:shadow-xl'
                    : 'border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <p className="text-center text-sm text-gray-500 mt-12">
          No credit card required for free tier. Cancel anytime.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
